<template>
	<v-container>
		<v-row align="start" align-content="start" justify="center">
			<v-col xl="4" lg="6" md="8" xs="12">
				<v-card class="d-flex flex-column pb-5">
					<v-toolbar color="primary" dark flat>
						<v-toolbar-title>{{ title }}</v-toolbar-title>
					</v-toolbar>

					<v-card-text v-if="state === 'loading'" class="pa-10 text-center">
						<v-progress-circular
							indeterminate
							color="secondary"
							size="48"
							class="mb-4"
						/>
						<div class="text-body-1">Signing you in...</div>
					</v-card-text>

					<v-card-text v-else class="pa-10 text-center">
						<v-icon color="secondary" size="48" class="mb-4">
							mdi-link-variant-off
						</v-icon>
						<div class="text-body-1 mb-2">
							This login link has expired or is invalid.
						</div>
						<div class="text-body-2 text-grey-darken-2">
							Please return to the login page and request a new link with "Email
							me a login link".
						</div>
						<v-btn
							class="bg-secondary mt-6"
							elevation="2"
							@click="goToLogin"
						>
							Go to login
						</v-btn>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import * as Sentry from "@sentry/vue";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import auth from "@/auth";
import { safeRedirectTarget } from "@/utils/redirect";

export default {
	name: "MagicLogin",

	beforeRouteEnter(to, from, next) {
		next((vm) => {
			// kick off the passwordless login once the component is mounted
			vm.processLogin(to.params.token, to.query.signature);
		});
	},

	emits: ["activate-activity-timer"],

	data() {
		return {
			// "loading" while we validate + sign in; "error" if the link is bad
			state: "loading",
			title: "Logging in",
		};
	},

	methods: {
		...mapActions(useMainStore, ["setUser", "setAuthenticated"]),
		goToLogin() {
			this.$router.push("/");
		},
		async processLogin(token, signature) {
			try {
				// validate the signed link and exchange it for the ENCRYPTED
				// token that /login expects
				const { data } = await this.$http.get(`/magic-login/${token}`, {
					params: { signature },
				});

				const encryptedToken = data.token;

				// drive the existing /login token-path - identical to the SAML
				// interstitial and VLogin.login(token)
				const response = await this.$http.post("/login", {
					token: encryptedToken,
				});

				this.setAuthenticated(auth.isAuthenticated());

				const payload = response.data.payload;

				// NOTE: deliberately does NOT set the `auto_login` cookie here
				// (DCTL-1951). `auto_login` drives the boot guard's SSO redirect
				// (main.js), which is wrong for passwordless prospects - it bounced
				// them to /sso-redirect on return. Persistence for remember-me
				// prospects is handled server-side by the 30-day flag + remember_web
				// cookies, so no client auto-login flag is needed. (SSO users still
				// set it legitimately in VLogin's token path.)

				// start the idle-session activity tracker (handled in App.vue)
				this.$emit("activate-activity-timer");

				// register logged in user
				this.setUser(payload.user);

				// set sentry user
				Sentry.setUser({ email: payload.user.email });

				// identify user to LaunchDarkly
				if (this.$ldClient) {
					this.$ldClient.identify({
						kind: "user",
						key: payload.user.uuid,
						email: payload.user.email,
						firstName: payload.user.first_name,
						lastName: payload.user.last_name,
						name: `${payload.user.first_name} ${payload.user.last_name}`,
					});
				}

				// redirect to the destination carried on the magic link (the
				// provenance page the prospect originally clicked), else home.
				// A prospect with no destination is steered to their getting-
				// started page by the nav guard rather than the app dashboard.
				const target = safeRedirectTarget(this.$route?.query?.redirect);
				if (target) {
					this.$router.replace(target);
				} else {
					this.$router.push("/home");
				}
			} catch (error) {
				// clear any partial auth state and show the expired/invalid view
				auth.clearAuthenticated();
				this.setAuthenticated(false);
				Sentry.setUser(null);
				this.state = "error";
			}
		},
	},
};
</script>

<style lang="scss"></style>
